import axios from 'axios';
import React, { Component } from 'react';

import { ToastContainer, toast } from 'react-toastify';
import Lightbox from 'react-awesome-lightbox';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import FullscreenRoundedIcon from '@material-ui/icons/FullscreenRounded';
import Navbar from '../../components/navBar/navBar';
import {
  apiList, verifyLogin, dencrypt, encrypt,
} from '../../helpers';
import { groupConstructionMediaByActivityName } from '../../helpers/utils';
import '../../assets/style/lightbox.css';
import '../../assets/style/progressReport.scss';

// Photos go into the progress report PDF in the order they are picked.
const MAX_PHOTOS = 6;
// Same groups and order as before: who uploaded the photo.
const UPLOADERS = [
  { key: 'self', label: 'Customer' },
  { key: 'cont', label: 'Contractor' },
  { key: 'pm', label: 'Relationship Manager' },
  { key: 'val', label: 'Valuer' },
];

class GenerateConstructionProgress extends Component {
  constructor() {
    super();
    this.state = {
      photos: [],
      selectedData: [],
      loaded: false,
      preview: null,
    };
  }

  componentDidMount() {
    this.getData();
  }

  getData() {
    const detailId = dencrypt(this.props.match.params.detailId);
    const detailIdP = JSON.parse(detailId);
    const { session } = verifyLogin();
    const config = { headers: { session } };
    axios.get(`${apiList.GET_PROPERTY_MEDIA}?id=${detailIdP.propertyId}`, config)
      .then((response) => {
        if (response && response.data && response.data.data) {
          const media = groupConstructionMediaByActivityName(response.data.data);
          this.setState({ photos: media });
        }
        this.setState({ loaded: true });
      })
      .catch((error) => {
        this.setState({ loaded: true });
        toast.error(`${error.message || error.response.message}`);
      });
  }

  // Select / unselect a photo (max 6), keeping the pick order.
  togglePhoto(subElement) {
    const { selectedData } = this.state;
    const selected = selectedData.find((item) => item.id === subElement.id);
    if (selected) {
      this.setState({ selectedData: selectedData.filter((item) => item.id !== subElement.id) });
    } else if (selectedData.length < MAX_PHOTOS) {
      this.setState({
        selectedData: [...selectedData, {
          id: subElement.id,
          url: subElement.url,
          date: subElement.submittedDate,
          lat: subElement.lat,
          lng: subElement.lng,
          by: subElement.submittedUserType,
        }],
      });
    }
  }

  generateReport() {
    const { selectedData } = this.state;
    if (selectedData.length < 1) return;
    const detailId = dencrypt(this.props.match.params.detailId);
    const detailIdP = JSON.parse(detailId);
    detailIdP.docType = 'loanProgress';
    detailIdP.photos = selectedData;
    const redirectUrl = `/document/${encrypt(JSON.stringify(detailIdP))}`;
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1000);
  }

  renderBar() {
    const { selectedData } = this.state;
    const count = selectedData.length;
    return (
      <header className='gp-card gp-bar'>
        <div className='gp-bar-row'>
          <div className='gp-bar-text'>
            <h1>Select photos for the progress report</h1>
            <p>{`Choose up to ${MAX_PHOTOS} photos. They appear in the report in the order you select them.`}</p>
          </div>
          <div className='gp-bar-actions'>
            <span className={`gp-count ${count === MAX_PHOTOS ? 'full' : ''}`}>
              <strong>{count}</strong>
              {` of ${MAX_PHOTOS} selected`}
            </span>
            <Button
              color='primary'
              disabled={count === 0}
              onClick={() => this.setState({ selectedData: [] })}
            >
              Clear
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={count === 0}
              onClick={() => this.generateReport()}
            >
              Generate report
            </Button>
          </div>
        </div>
        {count > 0 ? (
          <div className='gp-tray' aria-label='Selected photos, in report order'>
            {selectedData.map((item, index) => (
              <div className='gp-tray-item' key={item.id}>
                <img src={item.url} alt={`Selected ${index + 1}`} />
                <span className='gp-badge'>{index + 1}</span>
                <button
                  type='button'
                  className='gp-tray-remove'
                  aria-label={`Remove photo ${index + 1}`}
                  onClick={() => this.togglePhoto(item)}
                >
                  <CloseRoundedIcon />
                </button>
              </div>
            ))}
            {Array.from({ length: MAX_PHOTOS - count }, (_, i) => (
              <div className='gp-tray-slot' key={`slot-${i}`}>{count + i + 1}</div>
            ))}
          </div>
        ) : null}
      </header>
    );
  }

  renderTile(subElement, groupItems, groupTitle) {
    const { selectedData } = this.state;
    const order = selectedData.findIndex((item) => item.id === subElement.id);
    const isSelected = order !== -1;
    const locked = !isSelected && selectedData.length >= MAX_PHOTOS;
    const tile = (
      <div className={`gp-tile ${isSelected ? 'selected' : ''} ${locked ? 'locked' : ''}`}>
        <button
          type='button'
          className='gp-select'
          aria-pressed={isSelected}
          aria-label={isSelected ? `Photo ${order + 1} selected` : 'Select photo'}
          onClick={() => this.togglePhoto(subElement)}
        >
          <img src={subElement.url} alt={groupTitle} loading='lazy' />
        </button>
        {isSelected ? <span className='gp-badge'>{order + 1}</span> : null}
        <span className='gp-check'><CheckRoundedIcon /></span>
        <button
          type='button'
          className='gp-expand'
          aria-label='View larger'
          onClick={() => this.setState({
            preview: {
              images: groupItems.map((item) => ({ url: item.url, title: groupTitle })),
              index: groupItems.findIndex((item) => item.id === subElement.id),
            },
          })}
        >
          <FullscreenRoundedIcon />
        </button>
      </div>
    );
    return locked ? (
      <Tooltip key={subElement.id} title={`You can select up to ${MAX_PHOTOS} photos`} arrow>
        {tile}
      </Tooltip>
    ) : <React.Fragment key={subElement.id}>{tile}</React.Fragment>;
  }

  renderGroups() {
    const { photos, loaded } = this.state;
    const groups = photos
      .map((element) => ({
        element,
        uploaders: UPLOADERS
          .map(({ key, label }) => ({
            label,
            items: (element[key] || []).filter((item) => item.url),
          }))
          .filter(({ items }) => items.length > 0),
      }))
      .filter(({ uploaders }) => uploaders.length > 0);

    if (!loaded) return <div className='gp-card gp-empty'>Loading photos…</div>;
    if (!groups.length) return <div className='gp-card gp-empty'>No Photo found!</div>;

    return groups.map(({ element, uploaders }) => (
      <section className='gp-card' key={element.title}>
        <header className='gp-group-head'>
          <h2>{element.activityConfigName}</h2>
          <dl>
            <div>
              <dt>Floor Name</dt>
              <dd>{element.propertyComponentFloorName || '—'}</dd>
            </div>
            <div>
              <dt>Component</dt>
              <dd>{element.propertyComponentName || '—'}</dd>
            </div>
          </dl>
        </header>
        {uploaders.map(({ label, items }) => (
          <div className='gp-uploader' key={label}>
            <h3>
              {'Uploaded by '}
              <span>{label}</span>
            </h3>
            <div className='gp-grid'>
              {items.map((sub) => this.renderTile(sub, items, element.activityConfigName))}
            </div>
          </div>
        ))}
      </section>
    ));
  }

  render() {
    const { preview } = this.state;
    return (
      <div className='gp-page'>
        <ToastContainer />
        <Navbar MainProps={this.props} />
        <main className='gp-main'>
          {this.renderBar()}
          {this.renderGroups()}
        </main>
        {preview ? (
          <Lightbox
            onClose={() => this.setState({ preview: null })}
            images={preview.images}
            startIndex={Math.max(0, preview.index)}
            allowRotate={false}
            doubleClickZoom={0}
          />
        ) : null}
      </div>
    );
  }
}

export default GenerateConstructionProgress;
